import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, FileArchive } from 'lucide-react';
import { useMemoryStore } from '../../stores/memoryStore';
import FileTreeItem from './FileTreeItem';
import type { FileSection } from '../../types/memory';

const DEFAULT_COLLAPSED = new Set(['Daily Logs']);

export default function FileTree() {
  const { sections, selectedPath, searchQuery, setSelectedPath } = useMemoryStore();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set(DEFAULT_COLLAPSED));

  const toggleSection = (label: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    const q = searchQuery.toLowerCase();
    return sections.map(section => ({
      ...section,
      files: section.files.filter(f => f.name.toLowerCase().includes(q)),
    })).filter(section => section.files.length > 0 || (section.label === 'Transcripts' && section.count));
  }, [sections, searchQuery]);

  return (
    <div className="flex flex-col gap-1 py-2">
      {filteredSections.map((section: FileSection) => {
        const isCollapsed = collapsed.has(section.label) && !searchQuery.trim();
        const Chevron = isCollapsed ? ChevronRight : ChevronDown;
        const isTranscripts = section.label === 'Transcripts';

        return (
          <div key={section.label}>
            <button
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted hover:text-text-secondary transition-colors"
              onClick={() => toggleSection(section.label)}
            >
              <Chevron className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 text-left">{section.label}</span>
              <span className="text-text-muted font-normal normal-case tracking-normal">
                {isTranscripts ? section.count : section.files.length}
              </span>
            </button>

            {!isCollapsed && (
              <div className="ml-1">
                {isTranscripts ? (
                  <div className="flex items-center gap-2 py-1.5 px-2 text-sm text-text-muted">
                    <FileArchive className="h-4 w-4 shrink-0" />
                    <span>{section.count} transcript files</span>
                  </div>
                ) : (
                  section.files.map(file => (
                    <FileTreeItem
                      key={file.path}
                      file={file}
                      selected={selectedPath === file.path}
                      onSelect={setSelectedPath}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}

      {filteredSections.length === 0 && (
        <div className="px-2 py-4 text-sm text-text-muted text-center">
          No files match your search
        </div>
      )}
    </div>
  );
}
